import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store';
import { initializeAuth, selectAuthStatus, selectCurrentUser, selectRoles } from '@/store/slices/authSlice';
import { AUTH_STATUS } from '@/features/auth/constants';
import { Spinner } from '@shopify/polaris';
import { adminApi } from '@/features/admin/api';
import { generateFingerprint } from '@/features/admin/utils/fingerprint';
import DeviceBlockedPage from '@/features/admin/pages/DeviceBlockedPage';
import DevicePendingPage from '@/features/admin/pages/DevicePendingPage';

type DeviceStatus = 'idle' | 'checking' | 'verified' | 'blocked' | 'pending';

/**
 * AuthGuard: wraps all protected routes.
 *
 * On mount, dispatches initializeAuth() which calls GET /auth/me.
 * - While initializing: shows loading spinner
 * - If unauthenticated: redirects to /login
 * - If authenticated system user (super_admin): verifies device before rendering
 * - If authenticated: renders child routes normally
 */
const AuthGuard: React.FC = () => {
    const dispatch = useAppDispatch();
    const status = useAppSelector(selectAuthStatus);
    const location = useLocation();
    const user = useAppSelector(selectCurrentUser);
    const roles = useAppSelector(selectRoles);
    const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>('idle');
    const [isRetrying, setIsRetrying] = useState(false);
    const isSystemUser = user?.userType === 'system' && roles.includes('super_admin');

    const verifyDevice = useCallback(async (isRetry = false) => {
        try {
            if (isRetry) setIsRetrying(true);
            setDeviceStatus('checking');
            const fingerprint = await generateFingerprint();
            const result = await adminApi.verifyDevice(fingerprint);
            if (result.verified) {
                setDeviceStatus('verified');
            } else if (result.pendingApproval) {
                setDeviceStatus('pending');
            } else {
                setDeviceStatus('blocked');
            }
        } catch {
            setDeviceStatus('blocked');
        } finally {
            if (isRetry) setIsRetrying(false);
        }
    }, []);

    useEffect(() => {
        // Always revalidate session on mount
        dispatch(initializeAuth());
    }, [dispatch]);

    useEffect(() => {
        if (
            (status === AUTH_STATUS.AUTHENTICATED || status === AUTH_STATUS.MIGRATING) &&
            isSystemUser &&
            deviceStatus === 'idle'
        ) {
            verifyDevice();
        }
    }, [status, isSystemUser, deviceStatus, verifyDevice]);

    // ── Loading / initializing ──
    if (status === AUTH_STATUS.INITIALIZING) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Spinner />
            </div>
        );
    }

    // ── Not authenticated ──
    if (status === AUTH_STATUS.UNAUTHENTICATED) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // ── Super admin device verification ──
    if (isSystemUser) {
        if (deviceStatus === 'checking' || deviceStatus === 'idle') {
            return (
                <div className="flex items-center justify-center min-h-screen bg-[#FAFAFA]">
                    <div className="flex flex-col items-center text-center space-y-5">
                        <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                            <Spinner />
                        </div>
                        <div className="space-y-1.5">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Verifying your device
                            </h2>
                            <p className="text-sm text-gray-500">
                                Running security checks. This should only take a moment.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }
        if (deviceStatus === 'blocked') {
            return <DeviceBlockedPage />;
        }
        if (deviceStatus === 'pending') {
            return (
                <DevicePendingPage
                    onRetry={() => verifyDevice(true)}
                    isChecking={isRetrying}
                />
            );
        }
    }

    // AUTHENTICATED — let them through
    return <Outlet />;
};

export default AuthGuard;
